import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import {
  Recorder,
  TransferRecorderHistoryItem,
  TransferRecorderHistoryResponse,
} from "api/interfaces/recorderInterface.interface";
import Status from "components/blocks/status/Status";
import { StagingPageOutletContext } from "../StagingPage";
import { useEffect, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import {
  useLocation,
  useOutletContext,
  useSearchParams,
} from "react-router-dom";
import {
  BasePagination,
  LEVEL_TYPE,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { useQuery } from "react-query";
import * as mediaQuery from "components/MediaQuery";
import { convertType } from "utils/functions";
import { AlertRecorerTypeFilter } from "utils/AlertUtil";
import TableColumnFilterTitle from "components/blocks/table/TableColumnFilterTitle";
import Type from "components/atoms/text/labels/Type";
import { TableColumnsType } from "antd";
import { changeSortOrderAntd } from "components/atoms/table/AntdTable";
import { getAccountTimeFormatDate } from "utils/timeUtil";
import { ExpandableConfig } from "antd/es/table/interface";
import RecorderListExpand from "pages/recorders/recorders/list/RecorderListExpand";
import AccountId from "components/atoms/text/labels/AccountId";
import StagingHistoryPresenter from "./StagingHistoryPresenter";
import RecorderTitle from "components/blocks/recorder/RecorderTitle";
import Tabs from "components/atoms/tabs/Tabs";
import { STAGING_TABS } from "../StagingPage";
import { useMenu } from "hook/useMenu";
import { getTransferRecorderHistory } from "api/recorderAPI";
import { Row } from "../StagingStyled";

interface Props {}
export interface DataRow {
  targetAccountId: string;
  targetAccountName: string;
  transferredAt: string;
  transferredBy: string;
  registeredAt: string;
  registeredBy: string;
  type: string;
  title: string;
  recorderCount: number;
  licenseType: string;
  mergedSystemId: string;
  mergedSystemName: string;
  key: string;
  status: string;
  cloudSystemId?: string;
  version: string;
  onlineStatue?: boolean;
}

const initialQueryInfo = {
  ...baseQueryInfo,
  sortType: "recorderName",
};

export default function StagingHistoryContainer(props: Props) {
  const {
    onHeaderCell,
    onClickFilterTitle,
    onChangePage,
    setButtons,
    setCenterChildren,
    setTitleComponent,
    setClassName,
    setNoSearch,
  } = useOutletContext<StagingPageOutletContext>();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );
  const location = useLocation();

  const [searchParams] = useSearchParams();
  const [pageInfo, setPageInfo] = useState<Partial<BasePagination>>({});
  const { menuNavigation } = useMenu({});

  const recorderQueryInfo = useMemo<BasePagination>(() => {
    const get = (key: string) => searchParams.get(key);

    return {
      ...initialQueryInfo,
      ...pageInfo,
      ...(get("pageNum") && { pageNum: Number(get("pageNum")) }),
      ...(get("pageLimit") && { pageLimit: Number(get("pageLimit")) }),
      ...{ keyword: get("keyword") ?? "" },
      ...(get("sortType") && { sortType: get("sortType")! }),
      ...(get("sortDirection") && {
        sortDirection: get("sortDirection") as SORT_DIRECTION,
      }),
      ...(get("type") && { type: get("type") }),
    };
  }, [searchParams, pageInfo]);

  const recorderQueryKey = useQueryKey(recorderQueryInfo);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string | null>(null);
  const isMobile = mediaQuery.useIsMobile();

  const initialData = () => {
    setCenterChildren(
      <Status
        status={{
          totalTitle: "Total",
          totalValue: 0,
        }}
      />
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isLoading: isInitialLoading, data } = useQuery(
    ["stagingHistory", recorderQueryKey, selectedAccount.accountId],
    () =>
      getTransferRecorderHistory({
        payload: recorderQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      enabled: true,
      //refetchInterval: 10000,
      refetchOnWindowFocus: false,
      retry: 0,
      onSuccess: (res: TransferRecorderHistoryResponse) => {
        if (res.error !== 0 || !res.result) {
          initialData();
          return;
        }

        // setRecorder(res.result);

        setCenterChildren(
          <Status
            status={{
              totalTitle: "Total",
              totalValue: res.page.total,
            }}
          />
        );
        setPageInfo(res.page);
      },
      onError: () => {
        initialData();
      },
    }
  );
  const recorder = data?.result ?? [];

  const columns: TableColumnsType<TransferRecorderHistoryItem> = useMemo(
    () => [
      {
        key: "type",
        title: (
          <TableColumnFilterTitle
            title="Type"
            filters={AlertRecorerTypeFilter}
            onClick={(value: string | undefined) => {
              onClickFilterTitle(value, "type");
            }}
          />
        ),
        width: isMobile ? 100 : 130,
        render: (value, row) => (
          <Type
            type={convertType(row)}
            licenseType={row.licenseType}
            isCloudCam={row.isCloudCam}
          />
        ),
      },
      {
        title: "Title",
        sorter: true,
        key: "recorderName",
        defaultSortOrder: "ascend",
        sortOrder: changeSortOrderAntd(recorderQueryInfo, "recorderName"),
        onHeaderCell: onHeaderCell,
        render: (value, row) => (
          <RecorderTitle
            mergedSystemName={row.mergedSystemName}
            recorderName={row.recorderName}
            type={row.type}
            displayType={row.displayType}
            licenseType={row.licenseType}
          />
        ),
        ellipsis: true,
      },
      {
        key: "targetAccount",
        title: "Target Organization",
        render: (value, row) => (
          <Row>
            <AccountId accountId={row.toAccountNumber} level={LEVEL_TYPE.EU} />
            {row.toAccountName}
          </Row>
        ),
        width: "15%",
      },
      {
        title: "Transferred Date",
        render: (value, row) =>
          getAccountTimeFormatDate(
            row.transferredAt || "",
            selectedAccount,
            true,
            false
          ),
        responsive: ["xl"],
        width: "10%",
      },
      {
        title: "Transferred By",
        render: (value, row) => row.transferredBy,
        responsive: ["lg"],
        width: "15%",
      },
      {
        title: "Registered Date",
        render: (value, row) =>
          getAccountTimeFormatDate(
            row.registeredAt || "",
            selectedAccount,
            true,
            false
          ),
        align: "center",
        width: "10%",
      },
      {
        title: "Registered By",
        render: (value, row) => row.registeredBy,
        responsive: ["lg"],
        width: "15%",
      },
    ],
    [
      isMobile,
      onClickFilterTitle,
      onHeaderCell,
      recorderQueryInfo,
      selectedAccount,
    ]
  );

  const expandableProps = useMemo<ExpandableConfig<DataRow>>(() => {
    return {
      expandRowByClick: false,
      expandedRowKeys: expandedRowKeys ? [expandedRowKeys] : [],
      onExpand: (expanded: boolean, record: DataRow) => {
        if (expanded) {
          setExpandedRowKeys(record.mergedSystemId);
        } else {
          setExpandedRowKeys(null);
        }
      },
      expandedRowRender: (record: DataRow) => (
        <RecorderListExpand
          mergedSystemId={record.mergedSystemId}
          columns={columns as TableColumnsType<Recorder>}
          queryInfo={recorderQueryInfo}
        />
      ),
      rowExpandable: (record: DataRow) =>
        convertType(record) === "SPECTRUM" && record.recorderCount > 1,
    };
  }, [columns, expandedRowKeys, recorderQueryInfo]);

  useEffect(() => {
    setButtons(null);
    setTitleComponent(
      <Tabs
        tabs={[
          { label: "Recorder", value: STAGING_TABS.RECORDER },
          { label: "History", value: STAGING_TABS.HISTORY },
        ]}
        onChangeTab={(value) => {
          menuNavigation({
            to: value,
          });
        }}
        currentTab={location.pathname}
      />
    );
    setClassName("");
    setNoSearch(false);
  }, [setButtons]);
  return (
    <StagingHistoryPresenter
      columns={columns}
      data={recorder}
      paginationInfo={pageInfo as BasePagination}
      onChangePage={onChangePage}
      initialPending={isInitialLoading}
      // expandableProps={expandableProps}
    />
  );
}
